export const ADDRESS_COUNTRIES = [
  {
    code: "US",
    label: "United States",
    postalCodePattern: /^\d{5}$/,
    postalCodeHint: "12345",
  },
] as const;

export type SupportedAddressCountry = (typeof ADDRESS_COUNTRIES)[number]["code"];

export const DEFAULT_ADDRESS_COUNTRY: SupportedAddressCountry = "US";

export const ACTIVE_ADDRESS_COUNTRY: SupportedAddressCountry = "US";

export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;

export function normalizeCountryCode(value?: string | null): SupportedAddressCountry {
  const candidate = value?.trim().toUpperCase();
  return ADDRESS_COUNTRIES.some((entry) => entry.code === candidate)
    ? (candidate as SupportedAddressCountry)
    : DEFAULT_ADDRESS_COUNTRY;
}

export function normalizeUsStateCode(value?: string | null): string {
  const candidate = value?.trim().toUpperCase();
  if (!candidate) return "";

  const byCode = US_STATES.find((state) => state.code === candidate);
  if (byCode) return byCode.code;

  const byName = US_STATES.find((state) => state.name.toUpperCase() === candidate);
  return byName?.code ?? "";
}

export function isPostalCodeValid(country: string | undefined, postalCode: string): boolean {
  const normalizedCountry = normalizeCountryCode(country);
  const definition = ADDRESS_COUNTRIES.find((entry) => entry.code === normalizedCountry);

  if (!definition) {
    return true;
  }

  return definition.postalCodePattern.test(postalCode.trim());
}

export function postalCodeHint(country: string | undefined): string {
  const normalizedCountry = normalizeCountryCode(country);
  const definition = ADDRESS_COUNTRIES.find((entry) => entry.code === normalizedCountry);
  return definition?.postalCodeHint ?? "";
}
